import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, HttpError } from '@/api/client';
import type { Company, ProfileView, VehicleType } from '@/api/types';
import { BigCTA } from '@/components/BigCTA';
import { CompanyLogo, COMPANIES_ORDER, COMPANY_NAMES } from '@/components/CompanyLogo';
import { VehiclePicker } from '@/components/VehiclePicker';
import { LocationFields } from '@/components/LocationFields';
import { formatError } from '@/components/ErrorView';
import { canonicalCity, canonicalDistrict } from '@/data/geo';
import { uploadDraft } from '@/upload-store';

export function SmartFormPage() {
  const nav = useNavigate();
  const draft = uploadDraft.get();
  const [profile, setProfile] = useState<ProfileView | null>(null);
  const [company, setCompany] = useState<Company | undefined>(draft.company);
  const [vehicleType, setVehicleType] = useState<VehicleType>(draft.vehicleType ?? 'BICYCLE');
  const [city, setCity] = useState(draft.city ?? '');
  const [district, setDistrict] = useState(draft.district ?? '');
  const [longestTripKm, setLongestTripKm] = useState<string>(
    draft.longestTripKm != null ? String(draft.longestTripKm) : '',
  );
  const [shiftDate, setShiftDate] = useState(draft.shiftDate ?? '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isHistorical = draft.type === 'HISTORICAL';
  const today = new Date().toISOString().slice(0, 10);
  // Stable across retries: a fresh uuid per tap defeats the server's replay guard, so a
  // request that succeeded but whose response was lost gets re-submitted as a duplicate
  // and rejected with DUPLICATE — costing the user trust score for retrying.
  const idemKey = useRef(crypto.randomUUID());

  useEffect(() => {
    if (draft.files.length === 0) {
      nav('/upload', { replace: true });
      return;
    }
    api.getProfile().then((p) => {
      setProfile(p);
      // Functional updates: this callback closes over the mount-time values, so a plain
      // `if (!city)` check would overwrite whatever the user typed while it was in flight.
      if (p.defaultLocation.city) setCity((prev) => prev || p.defaultLocation.city || '');
      if (p.defaultLocation.district) setDistrict((prev) => prev || p.defaultLocation.district || '');
      if (!vehicleType || vehicleType === 'UNSET') {
        if (p.defaultVehicle && p.defaultVehicle !== 'UNSET') setVehicleType(p.defaultVehicle);
      }
    }).catch(() => { /* profile fetch failure is non-fatal */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    setError(null);
    if (!company) { setError('Выберите сервис'); return; }
    if (!vehicleType || vehicleType === 'UNSET') { setError('Выберите транспорт'); return; }

    const km = longestTripKm.trim() ? Number(longestTripKm.replace(',', '.')) : undefined;
    if (km != null && (isNaN(km) || km < 0 || km > 200)) {
      setError('Самая длинная поездка: число от 0 до 200');
      return;
    }
    // Without a date the backend stamps the shift with today, which then collides with
    // today's real report and trips the duplicate-shift-date check.
    if (isHistorical && !shiftDate) { setError('Укажите дату смены'); return; }
    if (shiftDate && shiftDate > today) { setError('Дата смены не может быть в будущем'); return; }

    setSubmitting(true);
    uploadDraft.update({ company, vehicleType, city, district, longestTripKm: km, shiftDate });
    try {
      const created = await api.createSubmission({
        company,
        vehicleType,
        // Same canonicalisation as the profile — the analytics bucket is keyed on these strings.
        city: canonicalCity(city) || undefined,
        district: canonicalDistrict(city, district) || undefined,
        type: draft.type ?? 'DAILY',
        longestTripKm: km,
        shiftDate: shiftDate || undefined,
        files: draft.files,
        idempotencyKey: idemKey.current,
      });
      uploadDraft.clear();
      nav(`/success/${created.id}`, { replace: true });
    } catch (e) {
      setError(formatError(e));
      if (e instanceof HttpError && e.status === 429) {
        // Per-user upload rate limit hit — already shown via formatError.
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <button
        type="button"
        onClick={() => nav(-1)}
        className="big-cta secondary"
        style={{ alignSelf: 'flex-start', minHeight: 'auto', padding: '8px 14px', fontSize: 14 }}
      >
        ← Назад
      </button>

      <div>
        <h1 className="page-title">Детали смены</h1>
        <p className="page-subtitle">{draft.files.length} скриншот{plural(draft.files.length)}</p>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="field">
        <div className="field-label">Где работали?</div>
        <div className="company-carousel">
          {COMPANIES_ORDER.map((c) => (
            <button
              key={c}
              type="button"
              className={`company-item${c === company ? ' active' : ''}`}
              onClick={() => setCompany(c)}
            >
              <CompanyLogo company={c} />
              <span>{COMPANY_NAMES[c]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <div className="field-label">Способ доставки</div>
        <VehiclePicker value={vehicleType} onChange={setVehicleType} />
      </div>

      <div className="field">
        <div className="field-label">Локация</div>
        <LocationFields
          city={city}
          district={district}
          onCity={setCity}
          onDistrict={setDistrict}
          districtPlaceholder="Район (опц.)"
          detectable
        />
        {profile?.defaultLocation.city && (
          <div className="field-hint">
            Из профиля: {profile.defaultLocation.city}
            {profile.defaultLocation.district ? `, ${profile.defaultLocation.district}` : ''}
          </div>
        )}
      </div>

      {isHistorical && (
        <div className="field">
          <label className="field-label" htmlFor="shiftDate">Дата смены</label>
          <input
            id="shiftDate"
            className="field-input"
            type="date"
            max={today}
            value={shiftDate}
            onChange={(e) => setShiftDate(e.target.value)}
          />
        </div>
      )}

      <div className="field">
        <div className="field-label">Самая длинная поездка, км</div>
        <input
          className="field-input"
          inputMode="decimal"
          pattern="[0-9.,]*"
          placeholder="например, 4.5"
          value={longestTripKm}
          onChange={(e) => setLongestTripKm(e.target.value)}
        />
      </div>

      <div className="sticky-cta">
        <BigCTA disabled={submitting || !company} onClick={submit}>
          {submitting ? 'Отправляем…' : 'Отправить на проверку'}
        </BigCTA>
      </div>
    </div>
  );
}

function plural(n: number): string {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return '';
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return 'а';
  return 'ов';
}
